import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError, formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  UploadCloud, FileText, Sparkles, Search, Download, Eye, Trash2,
  CheckCircle2, AlertTriangle, Loader2, X, Plus, Bot, ArrowRight,
} from "lucide-react";

/**
 * AI Voucher Entry — scanned invoices → Gemini extract → editable review
 * → duplicate-checked auto-post. See /app/backend/ai_voucher.py for the
 * server-side pipeline. Data-testids follow the pattern ``ai-voucher-*``
 * for automation.
 */

const STATUS_META = {
  uploaded:  { label: "Uploaded",   color: "bg-slate-100 text-slate-700",   icon: FileText },
  extracting:{ label: "Extracting", color: "bg-blue-100 text-blue-800",     icon: Loader2 },
  ready:     { label: "Ready",      color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  approved:  { label: "Posted",     color: "bg-indigo-100 text-indigo-800", icon: CheckCircle2 },
  error:     { label: "Error",      color: "bg-red-100 text-red-800",       icon: AlertTriangle },
  deleted:   { label: "Deleted",    color: "bg-slate-100 text-slate-500",   icon: Trash2 },
};

function confidenceBadge(conf) {
  const n = Number(conf ?? 1);
  if (n >= 0.9)  return { cls: "border-emerald-300 bg-emerald-50 text-emerald-800", label: `${Math.round(n * 100)}%` };
  if (n >= 0.7)  return { cls: "border-amber-300 bg-amber-50 text-amber-800",       label: `${Math.round(n * 100)}%` };
  return                { cls: "border-red-300 bg-red-50 text-red-800",              label: `${Math.round(n * 100)}%` };
}
function fieldRingCls(conf) {
  const n = Number(conf ?? 1);
  if (n >= 0.9) return "border-slate-200";
  if (n >= 0.7) return "border-amber-300 bg-amber-50/40";
  return "border-red-300 bg-red-50/40";
}
const vFrom = (obj, key) => (obj?.[key]?.value ?? "");
const cFrom = (obj, key) => (obj?.[key]?.confidence ?? 1);

function Inner() {
  const { activeId } = useCompany();
  const [drafts, setDrafts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [ledgers, setLedgers] = useState([]);
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const loadDrafts = useCallback(async () => {
    if (!activeId) return;
    try {
      const { data } = await api.get(`/companies/${activeId}/ai-voucher/list/all`);
      setDrafts(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [activeId]);

  useEffect(() => { loadDrafts(); }, [loadDrafts]);
  useEffect(() => {
    if (!activeId) return;
    api.get(`/companies/${activeId}/ledgers`).then(({ data }) => setLedgers(data)).catch(() => {});
  }, [activeId]);

  const selected = useMemo(
    () => drafts.find((d) => d.id === selectedId) || null,
    [drafts, selectedId],
  );

  // Filter for the left list
  const filtered = useMemo(() => {
    if (!q.trim()) return drafts;
    const needle = q.trim().toLowerCase();
    return drafts.filter((d) => {
      const e = d.extraction || {};
      const blob = [
        d.original_filename, d.voucher_no, d.status,
        vFrom(e, "voucher_no"), vFrom(e, "party_name"),
        vFrom(e, "supplier_gstin"), vFrom(e, "buyer_gstin"),
        vFrom(e, "date"), String(vFrom(e, "total")),
      ].join(" ").toLowerCase();
      return blob.includes(needle);
    });
  }, [drafts, q]);

  // Sequential upload (Question 4a): upload → extract → next file.
  const uploadFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    let successCount = 0;
    for (let i = 0; i < files.length; i += 1) {
      const f = files[i];
      const fd = new FormData();
      fd.append("file", f);
      try {
        toast.info(`Uploading ${i + 1} of ${files.length}: ${f.name}`);
        const { data: up } = await api.post(
          `/companies/${activeId}/ai-voucher/upload`, fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        toast.info(`Reading with Gemini AI: ${f.name}`);
        await api.post(`/companies/${activeId}/ai-voucher/${up.upload_id}/extract`);
        successCount += 1;
        if (i === 0) setSelectedId(up.upload_id);
      } catch (e) {
        toast.error(`${f.name}: ${formatApiError(e.response?.data?.detail)}`);
      }
    }
    setUploading(false);
    await loadDrafts();
    if (successCount > 0) toast.success(`Extracted ${successCount} of ${files.length} document(s)`);
  };

  const onFileChange = (e) => {
    uploadFiles(Array.from(e.target.files || []));
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(Array.from(e.dataTransfer.files || []));
  };

  return (
    <>
      <PageHeader
        title="AI Voucher Entry"
        subtitle="Upload scanned invoices → Gemini 3 Pro extracts every field → you review → auto-posted."
        icon={<Bot className="w-6 h-6 text-blue-700" />}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-4">
        {/* ── LEFT: uploader + list ─────────────────────────────────── */}
        <div className="space-y-3">
          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`relative rounded-xl border-2 border-dashed p-6 text-center transition-colors ${
              dragOver ? "border-blue-500 bg-blue-50" : "border-slate-300 bg-white"
            }`}
            data-testid="ai-voucher-dropzone"
          >
            <UploadCloud className="w-8 h-8 mx-auto text-blue-700 mb-2" />
            <div className="text-sm font-semibold text-slate-900">
              Drop invoices here or click to browse
            </div>
            <div className="text-xs text-slate-500 mt-1">
              PDF, JPG, JPEG, PNG · up to 25 MB per file · batch supported
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              multiple
              onChange={onFileChange}
              className="hidden"
              data-testid="ai-voucher-file-input"
              disabled={uploading}
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="mt-3 bg-blue-700 hover:bg-blue-800"
              data-testid="ai-voucher-btn-upload"
            >
              {uploading ? (<><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Processing…</>) : (
                <><Plus className="w-4 h-4 mr-1" /> Choose files</>
              )}
            </Button>
          </div>

          {/* Search + list */}
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="p-2 border-b border-slate-100 flex items-center gap-2">
              <Search className="w-4 h-4 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search invoice, party, GSTIN, amount…"
                className="flex-1 h-8 text-sm outline-none"
                data-testid="ai-voucher-search"
              />
              <span className="text-xs text-slate-400 font-mono">{filtered.length}</span>
            </div>
            <ul className="max-h-[70vh] overflow-y-auto divide-y divide-slate-100">
              {filtered.length === 0 && (
                <li className="p-6 text-center text-sm text-slate-500">
                  No uploads yet. Drop an invoice above to begin.
                </li>
              )}
              {filtered.map((d) => {
                const meta = STATUS_META[d.status] || STATUS_META.uploaded;
                const Icon = meta.icon;
                const e = d.extraction || {};
                return (
                  <li key={d.id}>
                    <button
                      onClick={() => setSelectedId(d.id)}
                      className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 ${
                        selectedId === d.id ? "bg-blue-50 border-l-2 border-blue-600" : ""
                      }`}
                      data-testid={`ai-voucher-row-${d.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">
                            {vFrom(e, "party_name") || d.original_filename}
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {vFrom(e, "voucher_type") || "—"} · {vFrom(e, "voucher_no") || "no #"} ·{" "}
                            {vFrom(e, "date") || "no date"}
                          </div>
                          {(d.warnings || []).length > 0 && (
                            <div className="mt-1 text-[11px] text-amber-700 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              {d.warnings.length} warning(s)
                            </div>
                          )}
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${meta.color}`}>
                            <Icon className={`w-3 h-3 ${d.status === "extracting" ? "animate-spin" : ""}`} />
                            {meta.label}
                          </span>
                          {vFrom(e, "total") > 0 && (
                            <div className="text-xs font-mono text-slate-700 mt-1">
                              {formatINR(vFrom(e, "total"))}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* ── RIGHT: review form ───────────────────────────────────── */}
        <div>
          {!selected && (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
              <Sparkles className="w-8 h-8 mx-auto text-blue-500 mb-2" />
              Pick an extracted document on the left to review and post.
            </div>
          )}
          {selected && (
            <ReviewPanel
              key={selected.id}
              draft={selected}
              ledgers={ledgers}
              activeId={activeId}
              onPosted={async () => { await loadDrafts(); }}
              onDeleted={async () => { setSelectedId(null); await loadDrafts(); }}
              refreshLedgers={() => api.get(`/companies/${activeId}/ledgers`).then(({ data }) => setLedgers(data))}
            />
          )}
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * ReviewPanel — editable form + confidence badges + duplicate check + post.
 * ─────────────────────────────────────────────────────────────────────── */
function ReviewPanel({ draft, ledgers, activeId, onPosted, onDeleted, refreshLedgers }) {
  const e = draft.extraction || {};
  const [form, setForm] = useState(() => ({
    voucher_type: vFrom(e, "voucher_type") || "Purchase",
    voucher_no: vFrom(e, "voucher_no") || "",
    date: vFrom(e, "date") || new Date().toISOString().slice(0, 10),
    party_name: vFrom(e, "party_name") || "",
    supplier_gstin: vFrom(e, "supplier_gstin") || "",
    buyer_gstin: vFrom(e, "buyer_gstin") || "",
    state: vFrom(e, "state") || "",
    place_of_supply: vFrom(e, "place_of_supply") || "",
    pan: vFrom(e, "pan") || "",
    taxable_value: Number(vFrom(e, "taxable_value") || 0),
    discount: Number(vFrom(e, "discount") || 0),
    freight: Number(vFrom(e, "freight") || 0),
    other_charges: Number(vFrom(e, "other_charges") || 0),
    cgst: Number(vFrom(e, "cgst") || 0),
    sgst: Number(vFrom(e, "sgst") || 0),
    igst: Number(vFrom(e, "igst") || 0),
    cess: Number(vFrom(e, "cess") || 0),
    round_off: Number(vFrom(e, "round_off") || 0),
    total: Number(vFrom(e, "total") || 0),
    payment_terms: vFrom(e, "payment_terms") || "",
    due_date: vFrom(e, "due_date") || "",
    currency: vFrom(e, "currency") || "INR",
    items: (e.items || []).map((it) => ({
      name: vFrom(it, "name"),
      hsn: vFrom(it, "hsn"),
      qty: Number(vFrom(it, "qty") || 0),
      unit: vFrom(it, "unit") || "Nos",
      rate: Number(vFrom(it, "rate") || 0),
      discount: Number(vFrom(it, "discount") || 0),
      gst_rate: Number(vFrom(it, "gst_rate") || 0),
      tax_amount: Number(vFrom(it, "tax_amount") || 0),
      amount: Number(vFrom(it, "amount") || 0),
    })),
    party_ledger_id:   draft.resolved_ledgers?.party_ledger_id || "",
    counter_ledger_id: draft.resolved_ledgers?.counter_ledger_id || "",
    cgst_ledger_id:    draft.resolved_ledgers?.cgst_ledger_id || "",
    sgst_ledger_id:    draft.resolved_ledgers?.sgst_ledger_id || "",
    igst_ledger_id:    draft.resolved_ledgers?.igst_ledger_id || "",
  }));
  const [warnings] = useState(draft.warnings || []);
  const [posting, setPosting] = useState(false);
  const [duplicate, setDuplicate] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const partyLedgers = useMemo(
    () => ledgers.filter((L) => /Sundry (Creditors|Debtors)|Bank|Cash|Current (Assets|Liabilities)/.test(L.group)),
    [ledgers],
  );
  const expenseLedgers = useMemo(
    () => ledgers.filter((L) => /Purchase Accounts|Direct Expenses|Indirect Expenses|Sales Accounts|Direct Income|Indirect Income|Other Income|Other Expenses/.test(L.group)),
    [ledgers],
  );
  const gstLedgers = useMemo(
    () => ledgers.filter((L) => /Duties & Taxes/.test(L.group)),
    [ledgers],
  );

  const setItem = (idx, k, v) => {
    setForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [k]: v };
      if (["qty", "rate", "discount", "gst_rate"].includes(k)) {
        const it = items[idx];
        const base = Number(it.qty) * Number(it.rate) - Number(it.discount || 0);
        items[idx].amount = +(base).toFixed(2);
        items[idx].tax_amount = +(base * Number(it.gst_rate || 0) / 100).toFixed(2);
      }
      return { ...f, items };
    });
  };
  const addItem = () => setForm((f) => ({
    ...f, items: [...f.items, { name: "", hsn: "", qty: 1, unit: "Nos", rate: 0, discount: 0, gst_rate: 18, tax_amount: 0, amount: 0 }],
  }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const post = async (force = false) => {
    if (!form.party_ledger_id) return toast.error("Select the party ledger.");
    if (!form.counter_ledger_id) return toast.error("Select the counter ledger (Purchase / Sales A/c).");
    setPosting(true);
    try {
      const { data } = await api.post(
        `/companies/${activeId}/ai-voucher/${draft.id}/approve${force ? "?skip_duplicate=true" : ""}`,
        form,
      );
      if (data.duplicate) {
        setDuplicate(data);
        setPosting(false);
        return;
      }
      toast.success(`Posted as ${data.voucher_no} · ${data.voucher_type || form.voucher_type}`);
      setDuplicate(null);
      await onPosted();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPosting(false);
    }
  };

  const deleteDraft = async () => {
    if (!window.confirm("Discard this AI voucher draft?")) return;
    try {
      await api.delete(`/companies/${activeId}/ai-voucher/${draft.id}`);
      toast.success("Discarded");
      await onDeleted();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const originalUrl = `${process.env.REACT_APP_BACKEND_URL}/api/companies/${activeId}/ai-voucher/${draft.id}/file`;
  const isPosted = draft.status === "approved";

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-4 py-2">
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-blue-700" />
          <div>
            <div className="text-sm font-semibold text-slate-900">{draft.original_filename}</div>
            <div className="text-xs text-slate-500">
              {draft.status} · uploaded {(draft.uploaded_at || "").slice(0, 16).replace("T", " ")}
              {draft.voucher_no && <> · posted as <b>{draft.voucher_no}</b></>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={originalUrl}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-slate-200 text-xs text-slate-700 hover:bg-slate-50"
            data-testid="ai-voucher-view-original"
          >
            <Eye className="w-3.5 h-3.5" /> View
          </a>
          <a
            href={originalUrl}
            download={draft.original_filename}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-slate-200 text-xs text-slate-700 hover:bg-slate-50"
            data-testid="ai-voucher-download-original"
          >
            <Download className="w-3.5 h-3.5" /> Download
          </a>
          {!isPosted && (
            <button
              onClick={deleteDraft}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-red-200 text-xs text-red-700 hover:bg-red-50"
              data-testid="ai-voucher-discard"
            >
              <Trash2 className="w-3.5 h-3.5" /> Discard
            </button>
          )}
        </div>
      </div>

      {/* Validation warnings */}
      {warnings.length > 0 && (
        <div className="border-2 border-amber-300 bg-amber-50 rounded-xl p-3" data-testid="ai-voucher-warnings">
          <div className="text-xs font-semibold text-amber-900 uppercase tracking-wider mb-1 flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" /> Validation
          </div>
          <ul className="text-xs text-amber-900 space-y-0.5 pl-4 list-disc">
            {warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      {/* Duplicate warning */}
      {duplicate && (
        <div className="border-2 border-red-400 bg-red-50 rounded-xl p-4" data-testid="ai-voucher-duplicate">
          <div className="font-semibold text-red-900 mb-1 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Possible duplicate
          </div>
          <div className="text-xs text-red-900">
            {duplicate.message} Matches: {duplicate.matches.map((m) => `${m.voucher_no} on ${m.date} (₹${m.total})`).join(", ")}
          </div>
          <div className="mt-3 flex gap-2">
            <Button size="sm" onClick={() => setDuplicate(null)} variant="outline">Cancel</Button>
            <Button
              size="sm"
              onClick={() => post(true)}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="ai-voucher-force-post"
            >
              Post anyway
            </Button>
          </div>
        </div>
      )}

      {/* Header grid — type, no, date, party, GSTIN */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <FieldEdit label="Voucher type" conf={cFrom(e, "voucher_type")}>
            <select
              value={form.voucher_type} onChange={(ev) => set("voucher_type", ev.target.value)}
              disabled={isPosted}
              className="h-9 px-2 rounded-md border border-slate-300 bg-white text-sm w-full"
              data-testid="ai-voucher-type"
            >
              {["Purchase", "Sales", "Debit Note", "Credit Note"].map((t) => <option key={t}>{t}</option>)}
            </select>
          </FieldEdit>
          <FieldEdit label="Invoice / Voucher #" conf={cFrom(e, "voucher_no")}>
            <Input value={form.voucher_no} onChange={(ev) => set("voucher_no", ev.target.value)} disabled={isPosted} data-testid="ai-voucher-no" />
          </FieldEdit>
          <FieldEdit label="Date" conf={cFrom(e, "date")}>
            <Input type="date" value={form.date} onChange={(ev) => set("date", ev.target.value)} disabled={isPosted} data-testid="ai-voucher-date" />
          </FieldEdit>
          <FieldEdit label="Party name" conf={cFrom(e, "party_name")} wide>
            <Input value={form.party_name} onChange={(ev) => set("party_name", ev.target.value)} disabled={isPosted} data-testid="ai-voucher-party-name" />
          </FieldEdit>
          <FieldEdit label="Supplier GSTIN" conf={cFrom(e, "supplier_gstin")}>
            <Input value={form.supplier_gstin} onChange={(ev) => set("supplier_gstin", ev.target.value.toUpperCase())} disabled={isPosted} data-testid="ai-voucher-supplier-gstin" />
          </FieldEdit>
          <FieldEdit label="Buyer GSTIN" conf={cFrom(e, "buyer_gstin")}>
            <Input value={form.buyer_gstin} onChange={(ev) => set("buyer_gstin", ev.target.value.toUpperCase())} disabled={isPosted} />
          </FieldEdit>
          <FieldEdit label="State" conf={cFrom(e, "state")}>
            <Input value={form.state} onChange={(ev) => set("state", ev.target.value)} disabled={isPosted} />
          </FieldEdit>
          <FieldEdit label="Place of supply" conf={cFrom(e, "place_of_supply")}>
            <Input value={form.place_of_supply} onChange={(ev) => set("place_of_supply", ev.target.value)} disabled={isPosted} />
          </FieldEdit>
          <FieldEdit label="PAN" conf={cFrom(e, "pan")}>
            <Input value={form.pan} onChange={(ev) => set("pan", ev.target.value.toUpperCase())} disabled={isPosted} />
          </FieldEdit>
        </div>
      </div>

      {/* Ledger recommendation */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
          Ledgers (AI-recommended, editable)
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <LedgerPicker label={form.voucher_type === "Sales" ? "Customer ledger" : "Supplier ledger"}
                       list={partyLedgers} value={form.party_ledger_id} onChange={(v) => set("party_ledger_id", v)} disabled={isPosted}
                       testId="ai-voucher-party-ledger" />
          <LedgerPicker label={form.voucher_type === "Sales" ? "Sales account" : "Purchase account"}
                       list={expenseLedgers} value={form.counter_ledger_id} onChange={(v) => set("counter_ledger_id", v)} disabled={isPosted}
                       testId="ai-voucher-counter-ledger" />
          <LedgerPicker label="CGST ledger" list={gstLedgers} value={form.cgst_ledger_id} onChange={(v) => set("cgst_ledger_id", v)} disabled={isPosted} />
          <LedgerPicker label="SGST ledger" list={gstLedgers} value={form.sgst_ledger_id} onChange={(v) => set("sgst_ledger_id", v)} disabled={isPosted} />
          <LedgerPicker label="IGST ledger" list={gstLedgers} value={form.igst_ledger_id} onChange={(v) => set("igst_ledger_id", v)} disabled={isPosted} />
        </div>
      </div>

      {/* Line items */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between">
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
            Line items ({form.items.length})
          </div>
          {!isPosted && (
            <button onClick={addItem} className="text-xs text-blue-700 hover:underline flex items-center gap-1" data-testid="ai-voucher-add-item">
              <Plus className="w-3.5 h-3.5" /> Add line
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Item</th>
                <th className="text-left px-2 py-2">HSN</th>
                <th className="text-right px-2 py-2">Qty</th>
                <th className="text-left px-2 py-2">Unit</th>
                <th className="text-right px-2 py-2">Rate</th>
                <th className="text-right px-2 py-2">Disc</th>
                <th className="text-right px-2 py-2">GST %</th>
                <th className="text-right px-2 py-2">Amount</th>
                {!isPosted && <th className="w-8" />}
              </tr>
            </thead>
            <tbody>
              {form.items.map((it, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="p-1"><Input value={it.name} onChange={(ev) => setItem(i, "name", ev.target.value)} className="h-8" disabled={isPosted} /></td>
                  <td className="p-1"><Input value={it.hsn} onChange={(ev) => setItem(i, "hsn", ev.target.value)} className="h-8 w-24 font-mono" disabled={isPosted} /></td>
                  <td className="p-1"><Input type="number" step="0.001" value={it.qty} onChange={(ev) => setItem(i, "qty", Number(ev.target.value))} className="h-8 w-20 text-right font-mono" disabled={isPosted} /></td>
                  <td className="p-1"><Input value={it.unit} onChange={(ev) => setItem(i, "unit", ev.target.value)} className="h-8 w-16" disabled={isPosted} /></td>
                  <td className="p-1"><Input type="number" step="0.01" value={it.rate} onChange={(ev) => setItem(i, "rate", Number(ev.target.value))} className="h-8 w-24 text-right font-mono" disabled={isPosted} /></td>
                  <td className="p-1"><Input type="number" step="0.01" value={it.discount} onChange={(ev) => setItem(i, "discount", Number(ev.target.value))} className="h-8 w-20 text-right font-mono" disabled={isPosted} /></td>
                  <td className="p-1"><Input type="number" step="0.01" value={it.gst_rate} onChange={(ev) => setItem(i, "gst_rate", Number(ev.target.value))} className="h-8 w-16 text-right font-mono" disabled={isPosted} /></td>
                  <td className="p-1 text-right font-mono">{formatINR(it.amount)}</td>
                  {!isPosted && (
                    <td className="p-1">
                      <button onClick={() => removeItem(i)} className="text-red-600 hover:text-red-800"><X className="w-4 h-4" /></button>
                    </td>
                  )}
                </tr>
              ))}
              {form.items.length === 0 && (
                <tr><td colSpan={9} className="text-center py-6 text-xs text-slate-400">No line items — add one to build the voucher.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Totals */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <NumField label="Taxable" conf={cFrom(e, "taxable_value")} value={form.taxable_value} onChange={(v) => set("taxable_value", v)} disabled={isPosted} />
        <NumField label="Discount" conf={cFrom(e, "discount")} value={form.discount} onChange={(v) => set("discount", v)} disabled={isPosted} />
        <NumField label="Freight" conf={cFrom(e, "freight")} value={form.freight} onChange={(v) => set("freight", v)} disabled={isPosted} />
        <NumField label="Other charges" conf={cFrom(e, "other_charges")} value={form.other_charges} onChange={(v) => set("other_charges", v)} disabled={isPosted} />
        <NumField label="CGST" conf={cFrom(e, "cgst")} value={form.cgst} onChange={(v) => set("cgst", v)} disabled={isPosted} />
        <NumField label="SGST" conf={cFrom(e, "sgst")} value={form.sgst} onChange={(v) => set("sgst", v)} disabled={isPosted} />
        <NumField label="IGST" conf={cFrom(e, "igst")} value={form.igst} onChange={(v) => set("igst", v)} disabled={isPosted} />
        <NumField label="Cess" conf={cFrom(e, "cess")} value={form.cess} onChange={(v) => set("cess", v)} disabled={isPosted} />
        <NumField label="Round-off" conf={cFrom(e, "round_off")} value={form.round_off} onChange={(v) => set("round_off", v)} disabled={isPosted} />
        <NumField label="Grand total" conf={cFrom(e, "total")} value={form.total} onChange={(v) => set("total", v)} disabled={isPosted} bold />
      </div>

      {/* Actions */}
      {!isPosted && (
        <div className="flex justify-end gap-2 sticky bottom-0 bg-slate-50/90 backdrop-blur border-t border-slate-100 p-3 -mx-2 rounded-xl">
          <Button variant="outline" onClick={deleteDraft} disabled={posting}>Cancel</Button>
          <Button
            onClick={() => post(false)}
            disabled={posting}
            className="bg-gradient-to-r from-blue-700 to-emerald-600 hover:from-blue-800 hover:to-emerald-700 text-white shadow-md"
            data-testid="ai-voucher-approve"
          >
            {posting ? (<><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Posting…</>) : (
              <>Approve &amp; Post <ArrowRight className="w-4 h-4 ml-1" /></>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

function FieldEdit({ label, conf, children, wide = false }) {
  const badge = confidenceBadge(conf);
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-xs text-slate-600">{label}</Label>
        <span className={`text-[9px] font-mono px-1 py-0.5 rounded border ${badge.cls}`}>{badge.label}</span>
      </div>
      <div className={`rounded-md border ${fieldRingCls(conf)}`}>{children}</div>
    </div>
  );
}

function LedgerPicker({ label, list, value, onChange, disabled, testId }) {
  return (
    <div>
      <Label className="text-xs text-slate-600 block mb-1">{label}</Label>
      <select
        value={value || ""} onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full h-9 px-2 rounded-md border border-slate-300 bg-white text-sm"
        data-testid={testId}
      >
        <option value="">— select —</option>
        {list.map((L) => (
          <option key={L.id} value={L.id}>{L.name} ({L.group})</option>
        ))}
      </select>
    </div>
  );
}

function NumField({ label, conf, value, onChange, disabled, bold }) {
  const badge = confidenceBadge(conf);
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-xs text-slate-600">{label}</Label>
        <span className={`text-[9px] font-mono px-1 py-0.5 rounded border ${badge.cls}`}>{badge.label}</span>
      </div>
      <Input
        type="number" step="0.01" value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={disabled}
        className={`h-9 text-right font-mono ${bold ? "font-bold text-blue-800" : ""} ${fieldRingCls(conf)}`}
      />
    </div>
  );
}

export default function AIVoucherEntry() {
  return <RequireCompany><Inner /></RequireCompany>;
}
